import {FC, useState, useEffect} from 'react'
import { Eye } from 'lucide-react'
import { GET } from '../../../../services/api'
import Swal from 'sweetalert2'
import { TableAllocationHistoryModal } from './TableAllocationHistoryModal'

type Table = {
  id: number
  environmentId: number
  tableTypeId: number
  tableNumber: string
  customCapacity?: number
  positionX: number | null
  positionY: number | null
  isActive: number
  notes: string | null
  tableType?: {
    id: number
    capacity: number
    description: string
  }
  currentStatus?: string
}

type Allocation = {
  id: number
  environmentTableId: number
  bookingId?: number | null
  queueId?: number | null
  clientName: string
  people: number
  phone?: string
  email?: string
  status: string
  allocation_source?: 'booking' | 'queue' | string
  queueDateAllocation?: string
}

type Props = {
  tables: Table[]
  onEdit: (table: Table) => void
  onDelete: (tableId: number) => void
  selectionMode?: boolean
  selectedTableIds?: number[]
  onToggleSelection?: (tableId: number) => void
  allocations?: Allocation[]
}

export const TableManagementList: FC<Props> = ({
  tables,
  onEdit,
  onDelete,
  selectionMode = false,
  selectedTableIds = [],
  onToggleSelection,
  allocations = [],
}) => {
  const [showHistoryModal, setShowHistoryModal] = useState(false)
  const [selectedTableForHistory, setSelectedTableForHistory] = useState<Table | null>(null)
  const [tablesWithFutureAllocs, setTablesWithFutureAllocs] = useState<Set<number>>(new Set())
  
  // Verificar alocações futuras ao carregar a lista de mesas
  const checkAllTablesFutureAllocations = async () => {
    const tablesWithAllocs = new Set<number>();
    
    for (const table of tables) {
      try {
        const res = await GET(`/table-management/table/${table.id}/future-allocations`)
        if (res?.hasFutureAllocations) {
          tablesWithAllocs.add(table.id)
        }
      } catch (error) {
        console.error(`Erro ao verificar alocações da mesa ${table.id}:`, error)
      }
    }
    
    setTablesWithFutureAllocs(tablesWithAllocs)
  }

  // Executar verificação quando as mesas carregarem
  useEffect(() => {
    if (tables.length > 0) {
      checkAllTablesFutureAllocations()
    }
  }, [tables])
  
  const isTableAllocated = (tableId: number) => {
    return allocations.some((alloc) => alloc.environmentTableId === tableId)
  }

  const getAllocationInfo = (tableId: number) => {
    return allocations.find((alloc) => alloc.environmentTableId === tableId)
  }

  const canDeleteTable = (tableId: number) => {
    return !tablesWithFutureAllocs.has(tableId)
  }

  const checkFutureAllocations = async (tableId: number) => {
    try {
      console.log('🔍 checkFutureAllocations chamada para tableId:', tableId)
      const res = await GET(`/table-management/table/${tableId}/future-allocations`)
      console.log('📦 Resposta completa:', res)
      console.log('📦 res.success:', res?.success)
      console.log('📦 res.hasFutureAllocations:', res?.hasFutureAllocations)
      console.log('📦 res.allocations:', res?.allocations)
      console.log('📦 alocações encontradas:', res?.allocations?.length)
      return res?.hasFutureAllocations || false
    } catch (error) {
      console.error('❌ Erro ao verificar alocações futuras:', error)
      return false
    }
  }

  const handleEdit = async (table: Table) => {
    console.log('✏️ handleEdit chamado para mesa:', table.tableNumber, 'ID:', table.id)
    const hasFutureAllocs = await checkFutureAllocations(table.id)
    console.log('✏️ hasFutureAllocs resultado:', hasFutureAllocs)
    if (hasFutureAllocs) {
      console.log('⛔ Mostrando alerta de alocações futuras')
      Swal.fire({
        icon: 'warning',
        title: 'Mesa Não Pode Ser Editada',
        html: `<strong>${table.tableNumber}</strong> possui alocações futuras e não pode ser editada. Você pode visualizar o histórico clicando no ícone de olho.`,
        confirmButtonText: 'Entendi',
        confirmButtonColor: '#3085d6',
      })
      return
    }
    console.log('✏️ Permitindo edição')
    onEdit(table)
  }

  const handleDelete = async (tableId: number) => {
    console.log('🗑️ handleDelete chamado para tableId:', tableId)
    const hasFutureAllocs = await checkFutureAllocations(tableId)
    console.log('🗑️ hasFutureAllocs resultado:', hasFutureAllocs)
    if (hasFutureAllocs) {
      console.log('⛔ Mostrando alerta de exclusão não permitida')
      Swal.fire({
        icon: 'error',
        title: 'Mesa Não Pode Ser Excluída',
        html: `Esta mesa estando no status de alocada, ocupada ou em limpeza, não poderá ser excluída. Você pode visualizar o histórico clicando no ícone de olho.`,
        confirmButtonText: 'Entendi',
        confirmButtonColor: '#3085d6',
      })
      return
    }
    console.log('🗑️ Permitindo exclusão')
    onDelete(tableId)
  }

  const handleViewHistory = (table: Table) => {
    setSelectedTableForHistory(table)
    setShowHistoryModal(true)
  }

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'available':
        return <span className='badge badge-success'>Disponível</span>
      case 'allocated':
        return <span className='badge badge-warning'>Alocada</span>
      case 'occupied':
        return <span className='badge badge-danger'>Ocupada</span>
      case 'cleaning':
        return <span className='badge badge-info'>Limpeza</span>
      default:
        return <span className='badge badge-secondary'>-</span>
    }
  }

  return (
    <>
      <div className='table-responsive'>
        <table className='table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3'>
          <thead>
            <tr className='fw-bold text-muted'>
              {selectionMode && <th className='min-w-50px'></th>}
              <th className='min-w-50px'>Número</th>
              <th className='min-w-100px'>Capacidade</th>
              <th className='min-w-100px'>Status</th>
              <th className='min-w-100px'>Situação</th>
              <th className='min-w-80px'>Tipo</th>
              <th className='min-w-200px'>Alocação</th>
              <th className='min-w-150px'>Observações</th>
              {!selectionMode && <th className='min-w-150px text-end'>Ações</th>}
            </tr>
          </thead>
          <tbody>
            {tables.length === 0 ? (
              <tr>
                <td colSpan={6} className='text-center py-5'>
                  <div className='d-flex flex-column align-items-center'>
                    <i className='bi bi-inbox fs-3x text-muted mb-3'></i>
                    <span className='text-muted'>Nenhuma mesa cadastrada</span>
                  </div>
                </td>
              </tr>
            ) : (
              tables.map((table) => (
                <tr key={table.id}>
                  {selectionMode && (
                    <td>
                      <div className='form-check form-check-custom form-check-solid'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          checked={selectedTableIds.includes(table.id)}
                          onChange={() => onToggleSelection?.(table.id)}
                        />
                      </div>
                    </td>
                  )}
                  <td>
                    <span className='fw-bold'>{table.tableNumber}</span>
                  </td>
                  <td>
                    <div className='d-flex align-items-center'>
                      <i className='bi bi-people-fill text-primary me-2'></i>
                      <span>
                        {table.customCapacity || table.tableType?.capacity || '-'} pessoa(s)
                        {table.customCapacity && (
                          <i
                            className='bi bi-star-fill text-warning ms-1'
                            title='Capacidade personalizada'
                          ></i>
                        )}
                      </span>
                    </div>
                  </td>
                  <td>{getStatusBadge(table.currentStatus)}</td>
                  <td>
                    {table.isActive === 1 ? (
                      <span className='badge badge-light-success'>Ativa</span>
                    ) : (
                      <span className='badge badge-light-danger'>Inativa</span>
                    )}
                  </td>
                  <td>
                    {isTableAllocated(table.id) ? (
                      <span className={`badge ${getAllocationInfo(table.id)?.allocation_source === 'queue' ? 'badge-light-info' : 'badge-light-primary'}`}>
                        {getAllocationInfo(table.id)?.allocation_source === 'queue' ? 'Fila' : 'Reserva'}
                      </span>
                    ) : (
                      <span className='text-muted'>-</span>
                    )}
                  </td>
                  <td>
                    {isTableAllocated(table.id) ? (
                      <div className='d-flex align-items-center'>
                        <i className='bi bi-check-circle-fill text-success me-2'></i>
                        <div className='d-flex flex-column'>
                          <span className='fw-bold text-success'>
                            {getAllocationInfo(table.id)?.clientName}
                          </span>
                          <small className='text-muted'>
                            {getAllocationInfo(table.id)?.people} pessoa(s)
                            {getAllocationInfo(table.id)?.phone && 
                              ` • ${getAllocationInfo(table.id)?.phone}`
                            }
                            {getAllocationInfo(table.id)?.queueDateAllocation && (
                              ` • ${new Date(getAllocationInfo(table.id)?.queueDateAllocation as string).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
                            )}
                          </small>
                        </div>
                      </div>
                    ) : (
                      <span className='text-muted'>
                        <i className='bi bi-dash-circle text-muted me-1'></i>
                        Disponível
                      </span>
                    )}
                  </td>
                  <td>
                    <span className='text-muted'>{table.notes || '-'}</span>
                  </td>
                  {!selectionMode && (
                    <td className='text-end'>
                      <button
                        className='btn btn-icon btn-bg-light btn-active-color-info btn-sm me-1'
                        onClick={() => handleViewHistory(table)}
                        title='Visualizar histórico de alocações'
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                        onClick={() => handleEdit(table)}
                        title='Editar'
                      >
                        <i className='bi bi-pencil-fill'></i>
                      </button>
                      {canDeleteTable(table.id) && (
                        <button
                          className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm'
                          onClick={() => handleDelete(table.id)}
                          title='Excluir'
                        >
                          <i className='bi bi-trash-fill'></i>
                        </button>
                      )}
                      {!canDeleteTable(table.id) && (
                        <button
                          className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm'
                          disabled
                          title='Mesa não pode ser excluída (possui alocações futuras)'
                          style={{ opacity: 0.5, cursor: 'not-allowed' }}
                        >
                          <i className='bi bi-trash-fill'></i>
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selectedTableForHistory && (
        <TableAllocationHistoryModal
          isOpen={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
          tableId={selectedTableForHistory.id}
          tableName={selectedTableForHistory.tableNumber}
          environmentId={selectedTableForHistory.environmentId}
        />
      )}
    </>
  )
}
